import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "@react/shared/ui/IconButton"
import Badge      from "@react/shared/ui/Badge"
import EmptyState from "@react/shared/ui/EmptyState"
import MarginBadge  from "./MarginBadge"
import CategoryPill from "./CategoryPill"
import { formatMoney } from "./StatsStrip"

const MAX_PILLS = 3
const GRID_COLUMNS = "110px minmax(0, 1fr) 80px 60px 128px 128px 80px 80px 76px"

const formatCoef = coef =>
  coef == null || isNaN(coef)
    ? "—"
    : `×${coef.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const RowActions = ({ blueprint, onDuplicate, onDelete }) => (
  <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
    <IconButton size="md" title={I18n.t("quotes.qline_service_blueprints.row.duplicate")} onClick={() => onDuplicate(blueprint)}>
      <FontAwesomeIcon icon="copy" />
    </IconButton>
    <IconButton tone="danger" size="md" title={I18n.t("quotes.qline_service_blueprints.row.delete")} onClick={() => onDelete(blueprint)}>
      <FontAwesomeIcon icon="trash" />
    </IconButton>
  </div>
)

const BlueprintRow = ({ blueprint, onEdit, onDuplicate, onDelete }) => (
  <div
    role      = "row"
    className = "group grid items-center gap-x-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
    onClick   = {() => onEdit(blueprint.id)}
  >
    <div>
      <Badge tone="neutral" variant="soft" size="xs" className="font-mono max-w-full truncate">
        {blueprint.ref_product_distrib || `S-${blueprint.id}`}
      </Badge>
    </div>

    <div className="min-w-0">
      <div className="font-medium text-slate-900 truncate" title={blueprint.name}>
        {blueprint.name}
      </div>
      {blueprint.category_breakdown && blueprint.category_breakdown.length > 0 && (
        <div className="flex items-center gap-1 mt-1">
          {blueprint.category_breakdown.slice(0, MAX_PILLS).map(entry => (
            <CategoryPill key={entry.category} category={entry.category} size="xs" />
          ))}
          {blueprint.category_breakdown.length > MAX_PILLS && (
            <span className="text-[11px] text-slate-400">
              +{blueprint.category_breakdown.length - MAX_PILLS}
            </span>
          )}
        </div>
      )}
    </div>

    <div className="text-sm text-slate-600 truncate">{blueprint.unit || "u"}</div>
    <div className="text-center text-slate-600 tabular-nums">{blueprint.line_count}</div>
    <div className="text-right tabular-nums text-slate-600 whitespace-nowrap">
      {formatMoney(blueprint.purchase_cents, blueprint.currency_symbol)}
    </div>
    <div className="text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap">
      {formatMoney(blueprint.sale_cents, blueprint.currency_symbol)}
    </div>
    <div className="text-right whitespace-nowrap text-sm font-medium tabular-nums text-slate-700">
      {formatCoef(blueprint.coef)}
    </div>
    <div className="text-right">
      <MarginBadge pct={blueprint.margin_pct} />
    </div>
    <div className="flex items-center justify-end" onClick={event => event.stopPropagation()}>
      <RowActions blueprint={blueprint} onDuplicate={onDuplicate} onDelete={onDelete} />
    </div>
  </div>
)

const HeaderRow = () => (
  <div
    role      = "row"
    className = "grid items-center gap-x-3 px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200"
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
  >
    <div>{I18n.t("quotes.qline_service_blueprints.table.ref")}</div>
    <div>{I18n.t("quotes.qline_service_blueprints.table.name")}</div>
    <div>{I18n.t("quotes.qline_service_blueprints.table.unit")}</div>
    <div className="text-center">{I18n.t("quotes.qline_service_blueprints.table.lines")}</div>
    <div className="text-right">{I18n.t("quotes.qline_service_blueprints.table.purchase")}</div>
    <div className="text-right">{I18n.t("quotes.qline_service_blueprints.table.sale_excl_tax")}</div>
    <div className="text-right">{I18n.t("quotes.qline_service_blueprints.table.coef")}</div>
    <div className="text-right">{I18n.t("quotes.qline_service_blueprints.table.margin")}</div>
    <div />
  </div>
)

const BlueprintsTable = ({ blueprints, onEdit, onDuplicate, onDelete }) => (
  <div className="overflow-x-auto" role="table">
    <HeaderRow />
    {blueprints.map(blueprint => (
      <BlueprintRow key={blueprint.id} blueprint={blueprint} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
    ))}
    {blueprints.length === 0 && (
      <EmptyState size="lg">{I18n.t("quotes.qline_service_blueprints.table.empty")}</EmptyState>
    )}
  </div>
)

export default BlueprintsTable
